/**
 * Scroll reveal. design.md 7.4.
 *
 * One IntersectionObserver for the whole document, created lazily and shared.
 * Elements opt in from markup with `data-reveal` (single) or
 * `data-reveal-group` (children stagger). Nothing else animates on scroll:
 * there is no parallax, no scrub, no library.
 *
 * The CSS hides an element only once this module has *armed* it
 * (`data-reveal-armed`). That means a reader without JavaScript, a reader with
 * reduced motion, and a build with `SCROLL_REVEAL` switched off all see the
 * content at full opacity from first paint, and no reveal can ever strand
 * content in an invisible state.
 *
 * Escape hatches kept from the playbook, plus one added:
 *   - no IntersectionObserver           -> reveal immediately
 *   - element taller than 60% of the viewport (one getBoundingClientRect in a
 *     layout effect, so the measurement is post-layout)  -> reveal immediately
 *   - element already inside the viewport at mount (deep link, restored
 *     scroll)                           -> reveal before paint, no transition
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { SCROLL_REVEAL } from './switches';

const ARMED = 'data-reveal-armed';
const REVEALED = 'data-revealed';
const OPTIONS: IntersectionObserverInit = {
  threshold: 0.15,
  rootMargin: '0px 0px -10% 0px',
};

let observer: IntersectionObserver | null = null;

function reveal(el: Element, instant = false): void {
  if (instant) el.setAttribute('data-reveal-instant', '');
  el.removeAttribute(ARMED);
  el.setAttribute(REVEALED, '');
}

function getObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === 'undefined') return null;
  if (!observer) {
    observer = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        obs.unobserve(entry.target);
        reveal(entry.target);
      }
    }, OPTIONS);
  }
  return observer;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Every element in `root` that has opted in, groups expanded to children. */
function targetsIn(root: ParentNode): Element[] {
  const out: Element[] = [];
  for (const node of root.querySelectorAll('[data-reveal]')) out.push(node);
  for (const group of root.querySelectorAll('[data-reveal-group]')) {
    let i = 0;
    for (const child of group.children) {
      if (!child.hasAttribute('style') || !(child as HTMLElement).style.getPropertyValue('--i')) {
        (child as HTMLElement).style.setProperty('--i', String(i));
      }
      out.push(child);
      i += 1;
    }
  }
  return out;
}

/**
 * Arm every opted-in element under `ref`. Call once per page render; it is
 * idempotent, because an armed or revealed element is skipped.
 */
export function useReveal(ref: React.RefObject<HTMLElement | null>, deps: unknown[] = []): void {
  const armed = useRef(new WeakSet<Element>());

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (!SCROLL_REVEAL || prefersReducedMotion()) return;

    const io = getObserver();
    const vh = window.innerHeight || 0;

    for (const el of targetsIn(root)) {
      if (armed.current.has(el) || el.hasAttribute(REVEALED)) continue;
      armed.current.add(el);

      if (!io) {
        reveal(el, true);
        continue;
      }

      const box = el.getBoundingClientRect();
      // Already on screen (deep link, restored scroll): show it before paint.
      if (box.top < vh && box.bottom > 0) {
        reveal(el, true);
        continue;
      }
      // Taller than 60% of the viewport: it can never satisfy the threshold.
      if (box.height > vh * 0.6) {
        reveal(el, true);
        continue;
      }

      el.setAttribute(ARMED, '');
      io.observe(el);
    }

    return () => {
      if (!io) return;
      for (const el of targetsIn(root)) {
        if (el.hasAttribute(ARMED)) {
          io.unobserve(el);
          el.removeAttribute(ARMED);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // The instant flag exists only to suppress the transition for one frame.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const frame = requestAnimationFrame(() => {
      for (const el of root.querySelectorAll('[data-reveal-instant]')) {
        el.removeAttribute('data-reveal-instant');
      }
    });
    return () => cancelAnimationFrame(frame);
  });
}
